import axios from 'axios';
import { get } from 'lodash';
import { clientUid, endPoint } from 'config/config';
import { clearStorage, getAuth, saveAuth } from 'service/local-data-provider';
import camelcaseKeys from 'camelcase-keys';

export class AccessTokenRevoke extends Error {}

export class PermissionDenied extends Error {}

const prettify = obj => {
  return camelcaseKeys(obj, { deep: true });
};

const API_VERSION = 'v3';

class ApiService {
  http;
  refreshTokenHttp;

  constructor() {
    this.http = this.createHttp();
    this.http.interceptors.request.use(value => this.requestInterceptor(value));
    this.http.interceptors.response.use(
      value => prettify(value),
      error => this.refreshTokenInterceptor(error)
    );
    this.refreshTokenHttp = this.createHttp();
    this.refreshTokenHttp.interceptors.request.use(value => this.requestInterceptor(value));
    this.refreshTokenHttp.interceptors.response.use(
      value => prettify(value),
      error => error
    );
  }

  auth = (username, password, subAccountUsername) => {
    return this.http.post(`${API_VERSION}/login`, {
      username,
      password,
      sub_account_username: subAccountUsername,
    });
  };

  logout = () => {
    return this.http.post(`${API_VERSION}/logout`);
  };

  changePassword = (oldPassword, newPassword) => {
    return this.http.post(`${API_VERSION}/me/password`, {
      password: oldPassword,
      new_password: newPassword,
    });
  };

  authTotp = totp => {
    return this.http.post(`${API_VERSION}/two_factor_auth/auth`, {
      totp_code: totp,
    });
  };

  userInfo = () => {
    return this.http.get(`${API_VERSION}/me`);
  };

  updateUserInfo = (email, displayName, receipt) => {
    return this.http.post(`${API_VERSION}/me`, {
      email: email,
      display_name: displayName,
      receipt: receipt,
    });
  };

  fetchLicensePlates = ({ plateNumberCont, page, perPage }) => {
    return this.http.get(`${API_VERSION}/enterprise/license_plates`, {
      params: {
        page,
        per_page: perPage,
        order_by: 'plate_number_asc',
        plate_number_cont: plateNumberCont,
      },
    });
  };

  createLicensePlate = ({ plateNumber, energySource, vehicleType, oilType }) => {
    return this.http.post(`${API_VERSION}/enterprise/license_plates`, {
      plate_number: plateNumber,
      energy_source: energySource,
      vehicle_type: vehicleType,
      oil_type: oilType,
    });
  };

  deleteLicensePlate = id => {
    return this.http.delete(`${API_VERSION}/enterprise/license_plates/${id}`);
  };

  updateLicensePlate = ({ id, plateNumber, energySource, vehicleType, oilType }) => {
    return this.http.put(`${API_VERSION}/enterprise/license_plates/${id}`, {
      plate_number: plateNumber,
      energy_source: energySource,
      vehicle_type: vehicleType,
      oil_type: oilType,
    });
  };

  initMochipayValidation = () => {
    return this.http.post(`${API_VERSION}/wallets/init_mochipay_validation`);
  };

  bindMochipayWallet = (token, transactionId) => {
    return this.http.post(`${API_VERSION}/wallets/mochipay_wallet`, {
      token: token,
      transaction_id: transactionId,
      default_distributor: true,
    });
  };

  deleteCreditCard = () => {
    return this.http.delete(`${API_VERSION}/wallets/credit_cards`);
  };

  fetchDebitAccountTransactions = (id, startAt, endAt, page, totalPage) => {
    return this.http.get(`${API_VERSION}/debit_accounts/${id}/transactions`, {
      params: {
        start_at: startAt,
        end_at: endAt,
        page: page,
        per_page: totalPage,
      },
    });
  };

  sendDebitCardReport = (id, startAt, endAt) => {
    return this.http.post(`${API_VERSION}/debit_accounts/${id}/transactions/export`, {
      start_at: startAt,
      end_at: endAt,
    });
  };

  fetchTwoFactorAuth = () => {
    return this.http.get(`${API_VERSION}/two_factor_auth`);
  };

  enableTwoFactorAuth = totp => {
    return this.http.post(`${API_VERSION}/two_factor_auth/enable`, {
      totp_code: totp,
    });
  };

  disableTwoFactorAuth = () => {
    return this.http.post(`${API_VERSION}/two_factor_auth/disable`);
  };

  fetchOrders = (page, pageSize, transactionTypes, plateNumber, startAt, endAt) => {
    return this.http.get(`${API_VERSION}/orders/simple`, {
      params: {
        transaction_types: transactionTypes,
        plate_number: plateNumber,
        start_at: startAt,
        end_at: endAt,
        page: page,
        per_page: pageSize,
      },
    });
  };

  fetchOrderDetail = url => {
    return this.http.get(url);
  };

  sendUserReport = (transactionTypes, startAt, endAt, plateNumber) => {
    return this.http(`${API_VERSION}/user_reports`, {
      params: {
        transaction_types: transactionTypes,
        start_at: startAt,
        end_at: endAt,
        plate_number: plateNumber,
      },
    });
  };

  fetchMochiCoinTransactions = (state, page, pageSize) => {
    return this.http.get(`${API_VERSION}/mochi_coin_transactions`, {
      params: {
        state: state,
        page: page,
        per_page: pageSize,
      },
    });
  };

  fetchEnterpriseDrivers = ({ page, perPage, mobileCont }) => {
    return this.http.get(`${API_VERSION}/enterprise_drivers`, {
      params: {
        page,
        per_page: perPage,
        mobile_cont: mobileCont,
      },
    });
  };

  fetchEnterpriseDriversRefuelingAmountInfo = () => {
    return this.http.get(`${API_VERSION}/enterprise_drivers/refueling_total_amount`);
  };

  updateEnterpriseLicensePlateTaxIdNumber = ({ licensePlateId, taxIdNumber }) => {
    return this.http.post(`${API_VERSION}/enterprise/vehicle_tax_setting_timelines`, {
      user_vehicle_ship_id: licensePlateId,
      tax_id_number: taxIdNumber,
    });
  };

  createEnterpriseDriver = ({ mobile }) => {
    return this.http.post(`${API_VERSION}/enterprise_drivers`, {
      mobile,
    });
  };

  fetchEnterpriseTaxSettings = () => {
    return this.http.get(`${API_VERSION}/enterprise/tax_settings`);
  };

  deleteEnterpriseDriver = ({ id }) => {
    return this.http.delete(`${API_VERSION}/enterprise_drivers/${id}`);
  };

  fetchCoupons = ({ state, page, perPage }) => {
    return this.http.get(`${API_VERSION}/promotion_certificates`, {
      params: {
        state,
        page,
        per_page: perPage,
      },
    });
  };

  createHttp = () =>
    axios.create({
      baseURL: endPoint,
      timeout: 30000,
      validateStatus: status => {
        return status >= 200 && status < 300;
      },
    });

  requestInterceptor = config => {
    const accessToken = this.accessToken();
    if (accessToken) {
      config.headers['Authorization'] = accessToken;
    }
    config.headers['Mochipay-Client-Id'] = clientUid;
    return config;
  };

  accessToken = () => {
    const accessToken = get(getAuth(), 'accessToken', undefined);
    return accessToken ? `Bearer ${accessToken}` : '';
  };

  refreshTokenInterceptor = error => {
    const {
      config: originalRequest,
      response: { status },
    } = error;
    if (status === 401) {
      return new Promise((resolve, reject) => {
        this.refreshTokenHttp
          .post(`/oauth/token`, {
            refresh_token: get(getAuth(), 'refreshToken', ''),
            grant_type: 'refresh_token',
          })
          .then(value => {
            if (value.isAxiosError) {
              const status = get(value, 'response.status');
              if (status === 401) {
                clearStorage();
                window.location.href = '/';
              } else {
                window.location.href = '/';
                reject(value);
              }
            } else {
              saveAuth(value.data);
              this.http.defaults.headers.Authorization = `${this.accessToken()}`;
              originalRequest.headers.Authorization = `${this.accessToken()}`;
              resolve(this.http(originalRequest));
            }
          })
          .catch(reason => {
            console.log(reason);
            reject(reason);
          });
      });
    } else if (status === 403) {
      return Promise.reject(new PermissionDenied());
    } else {
      return Promise.reject(error);
    }
  };
}

export default new ApiService();
